import React from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

export interface IOpioidRes {
  msg: string;
  data: {
    isPatientCurrentlyPrescribed: string;
    hasPatientTakeMedication: string;
    hasPatientTakeOtherMedication: string[];
    askFirstQuestion?: string;
    askMedicationQuestion?: string;
  };
  status: string;
}

const useFetchOpioidReport = (billingCycleId: string) => {
  const [opioidData, setOpioidData] = React.useState<IOpioidRes["data"] | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/pdf/v3/opioid/${billingCycleId}`,
    method: "GET",
  });

  const getOpioidData = React.useCallback(async () => {
    if (!billingCycleId) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        IOpioidRes
      >();

      if (apiError) {
        throw new Error(apiError.msg || "Failed to fetch opioid data");
      }

      if (response?.status === "success") {
        setOpioidData(response.data);
        return response.data;
      } else {
        throw new Error(response?.msg || "No opioid data available");
      }
    } catch (error: any) {
      console.error("Error in getOpioidData:", error);
      setError(error.message);
      // toast.error(error.message || "Unable to load opioid data. Please refresh and try again.");
      setOpioidData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [billingCycleId, makeApiCall]);

  // Automatically fetch when the component mounts
  React.useEffect(() => {
    if (billingCycleId) {
      getOpioidData();
    }
  }, [billingCycleId, getOpioidData]);

  return {
    opioidData,
    getOpioidData,
    isLoading,
    error,
    refetch: getOpioidData,
  };
};

export default useFetchOpioidReport;
